import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DoughnutProgressCard = ({ value = 75, label = 'Progress' }) => {
    const percentage = Math.min(Math.max(value, 0), 100); // Clamp between 0-100

    // Determine progress color based on percentage
    const getProgressColor = (percent) => {
        if (percent >= 80) return '#4CAF50'; // Green for high progress
        if (percent >= 60) return '#FF9800'; // Orange for medium progress
        if (percent >= 40) return '#FFC107'; // Yellow for low-medium progress
        return '#F44336'; // Red for low progress
    };

    const progressColor = getProgressColor(percentage);

    return (
        <Card sx={{ 
            width: '100%', 
            height: '180px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            color: 'white',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            },
            borderRadius: 4
        }}>
            {/* Animated background pattern */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                animation: 'pulse 3s ease-in-out infinite alternate',
                '@keyframes pulse': {
                    '0%': { opacity: 0.3 },
                    '100%': { opacity: 0.7 },
                }
            }} />
            
            <CardContent sx={{ 
                padding: 0, 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                position: 'relative',
                zIndex: 1,
                mt: 2
            }}>
                {/* Header with icon and label */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 2,
                    opacity: 0.9
                }}>
                    {percentage >= 100 ? (
                        <CheckCircleIcon sx={{ fontSize: 24, color: '#4CAF50' }} />
                    ) : (
                        <TrendingUpIcon sx={{ fontSize: 24, color: 'rgba(255,255,255,0.8)' }} />
                    )}
                    <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        {label}
                    </Typography>
                </Box>

                {/* Progress percentage */}
                <Typography sx={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    mb: 1,
                    fontFamily: 'Inter, sans-serif'
                }}>
                    {percentage}%
                </Typography>

                {/* Progress bar */}
                <Box sx={{ 
                    width: '80%', 
                    maxWidth: 200,
                    position: 'relative'
                }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={percentage}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: `linear-gradient(90deg, ${progressColor} 0%, ${progressColor}dd 100%)`,
                                boxShadow: `0 0 10px ${progressColor}66`,
                                animation: 'progressGlow 2s ease-in-out infinite alternate',
                                '@keyframes progressGlow': {
                                    '0%': { boxShadow: `0 0 5px ${progressColor}66` },
                                    '100%': { boxShadow: `0 0 15px ${progressColor}99` },
                                }
                            }
                        }}
                    />
                    
                    {/* Progress indicator dots */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        mt: 0.5
                    }}>
                        {[0, 25, 50, 75, 100].map((mark) => (
                            <Box key={mark} sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: percentage >= mark ? progressColor : 'rgba(255,255,255,0.3)',
                                transition: 'all 0.3s ease'
                            }} />
                        ))}
                    </Box>
                </Box>

                {/* Status text */}
                <Typography sx={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.7)',
                    mt: 1,
                    fontWeight: 500,
                    textAlign: 'center'
                }}>
                    {percentage >= 100 ? 'Task Completed!' : 
                     percentage >= 80 ? 'Almost there!' :
                     percentage >= 60 ? 'Good progress!' :
                     percentage >= 40 ? 'Keep going!' :
                     percentage >= 20 ? 'Getting started!' : 'Just beginning!'}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default DoughnutProgressCard;
